import React, { ReactNode, useMemo } from "react";

import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";

import { InputSkeleton, Skeleton, TextSkeleton } from "Cobalt/Skeleton";
import Stack from "Cobalt/Stack";
import AgentHeader from "Common/Agent/AgentHeader";
import { rtkQueryReponseToSubmissionError } from "Common/forms";
import NavigationControls from "Common/NavigationControls";
import { Surface, SurfaceHeader, SurfaceSection } from "Theme/Surface";

type ApplicationFormPageProps<V> = {
  // layout
  title?: ReactNode;
  agentMessage?: ReactNode;

  // data / lifecycle
  isLoading?: boolean;
  initialValues: V;
  onSubmit: (values: V) => Promise<unknown>;
  onBack?: () => void;
  saveDisabled?: boolean;

  /**
   * Forces a full remount of the underlying `<Form>` when the value changes.
   * Use when data behind the form changes in a way that should discard
   * dirty user input instead of merging via `keepDirtyOnReinitialize`.
   */
  formKey?: string | number;

  // form composition
  // Will maybe bring in later if necessary
  // formProps?: Partial<FormProps<V>>; // for the rare page that needs extra mutators/decorators
  children: ReactNode;
};

type ApplicationPageSkeletonProps = {
  hasTitle?: boolean;
  hasAgentMessage?: boolean;
  sections?: number;
};

export const ApplicationPageSkeleton = ({ hasTitle = true, hasAgentMessage = false, sections = 2 }: ApplicationPageSkeletonProps) => (
  <>
    {hasTitle && (
      <SurfaceHeader>
        <Skeleton className="h-6 w-4/12" />
      </SurfaceHeader>
    )}
    {hasAgentMessage && (
      <SurfaceSection>
        <Stack>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-10/12" />
          <Skeleton className="h-4 w-8/12" />
        </Stack>
      </SurfaceSection>
    )}
    {Array.from({ length: sections }).map((_, i) => (
      <SurfaceSection key={i}>
        <TextSkeleton className="" />
        <Stack gap="spacious">
          <InputSkeleton type="text" className="" />
          <InputSkeleton type="text" className="" />
          <InputSkeleton type="radio" className="" />
        </Stack>
      </SurfaceSection>
    ))}
    <SurfaceSection>
      <Stack direction="horizontal" justify="space-between">
        <Skeleton className="h-10 w-3/12" />
        <Skeleton className="h-10 w-3/12" />
      </Stack>
    </SurfaceSection>
  </>
);

const focusOnErrors = createDecorator();

export function ApplicationFormPage<V>({
  title,
  agentMessage,
  isLoading = false,
  initialValues,
  onSubmit,
  onBack,
  saveDisabled,
  formKey,
  children,
}: ApplicationFormPageProps<V>) {
  const handleSubmit = useMemo(
    () => async (values: V) => {
      try {
        return await onSubmit(values);
      } catch (error) {
        return rtkQueryReponseToSubmissionError({ error });
      }
    },
    [onSubmit],
  );

  return (
    <Surface>
      {isLoading ? (
        <ApplicationPageSkeleton hasTitle={!!title} hasAgentMessage={!!agentMessage} />
      ) : (
        <>
          {title && <SurfaceHeader>{title}</SurfaceHeader>}
          {agentMessage && (
            <SurfaceSection>
              <AgentHeader>{agentMessage}</AgentHeader>
            </SurfaceSection>
          )}

          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            decorators={[focusOnErrors]}
            mutators={{ ...arrayMutators }}
            keepDirtyOnReinitialize
          >
            {({ handleSubmit: rffSubmit, submitting }) => (
              <form onSubmit={rffSubmit}>
                {children}
                <NavigationControls onClickBack={onBack} submitting={submitting} saveDisabled={saveDisabled} />
              </form>
            )}
          </Form>
        </>
      )}
    </Surface>
  );
}
